import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Sparkles, CheckCircle2, Edit3, AlertTriangle,
  Lock, FileSignature, Clock, User, Brain, ShieldCheck,
  ChevronDown, ChevronUp, Plus, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { patientDetails, paiBuilderData } from '@/data/mockCaseManagerData';

const livelloColors = {
  critico: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100', dot: 'bg-red-500' },
  severo: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-100', dot: 'bg-orange-500' },
  moderato: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-100', dot: 'bg-amber-500' },
  lieve: { bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-100', dot: 'bg-slate-400' },
};

/* ═══════════════════════════════
   LEFT: Sintesi InterRAI/UVM
   ═══════════════════════════════ */
function InterRAISummary({ findings, patient }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl h-fit" data-testid="interrai-summary">
      <CardContent className="p-6">
        <button onClick={() => setExpanded(!expanded)} className="flex items-center justify-between w-full mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-50 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-teal-500" />
            </div>
            <div className="text-left">
              <h2 className="text-sm font-semibold text-foreground">Sintesi Valutazione UVM</h2>
              <p className="text-[10px] text-muted-foreground">InterRAI HC — Criticita emerse</p>
            </div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>

        {/* Patient mini header */}
        <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/30 mb-4">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-300">{patient.cognome[0]}{patient.nome[0]}</span>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground">{patient.cognome} {patient.nome}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="outline" className="text-[9px]">{patient.cia}</Badge>
              <span className="text-[10px] text-muted-foreground">{patient.diagnosi}</span>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="space-y-2.5 overflow-hidden"
            >
              {findings.map((f, i) => {
                const colors = livelloColors[f.livello] || livelloColors.lieve;
                return (
                  <div key={i} className={`p-3.5 rounded-xl border ${colors.border} ${colors.bg}`} data-testid={`finding-${i}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                      <span className={`text-xs font-bold ${colors.text}`}>{f.area}</span>
                      <Badge className={`text-[8px] border-0 ml-auto ${colors.bg} ${colors.text}`}>{f.livello}</Badge>
                    </div>
                    <p className="text-[11px] text-foreground font-medium">{f.dettaglio}</p>
                    <p className="text-[10px] text-muted-foreground mt-1 italic">{f.raccomandazione}</p>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════
   RIGHT: AI Suggestion Box
   ═══════════════════════════════ */
function AISuggestionBox({ aiData, onAccept, onModify, accepted, modifying }) {
  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden" data-testid="ai-suggestion-box">
      {/* AI Header with gradient accent */}
      <div className="relative px-6 pt-6 pb-5">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 via-indigo-500/5 to-cyan-500/5" />
        <div className="relative flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shadow-sm">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                Suggerimento AI
                {accepted && <Badge className="bg-emerald-50 text-emerald-600 border-0 text-[9px]">Accettato</Badge>}
              </h2>
              <p className="text-[10px] text-muted-foreground">{aiData.modello} · Confidenza {aiData.confidenza}%</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Clock className="w-3 h-3" />
              {aiData.generatoIl}
            </div>
          </div>
        </div>
      </div>

      <CardContent className="px-6 pb-6 pt-0">
        {/* Confidence bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-muted-foreground font-medium">Livello di confidenza del modello</span>
            <span className="text-xs font-bold text-violet-600">{aiData.confidenza}%</span>
          </div>
          <div className="h-2 bg-muted/40 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-400"
              initial={{ width: 0 }}
              animate={{ width: `${aiData.confidenza}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              data-testid="confidence-bar"
            />
          </div>
        </div>

        {/* Suggested interventions table */}
        <div className="rounded-xl border border-border/40 overflow-hidden mb-4" data-testid="ai-interventions">
          <div className="grid grid-cols-[1fr_auto_2fr] gap-0 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30 px-4 py-2.5">
            <span>Professionista</span>
            <span>Frequenza</span>
            <span>Prestazione</span>
          </div>
          {aiData.interventi.map((int, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_2fr] gap-0 px-4 py-3 border-t border-border/30 hover:bg-muted/10 transition-colors" data-testid={`intervention-${i}`}>
              <div className="flex items-center gap-2">
                <User className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                <span className="text-xs font-semibold text-foreground">{int.professionista}</span>
              </div>
              <Badge className="bg-violet-50 text-violet-600 border-0 text-[9px] font-semibold mx-3 whitespace-nowrap">{int.frequenza}</Badge>
              <span className="text-[11px] text-muted-foreground">{int.prestazione}</span>
            </div>
          ))}
        </div>

        {/* Duration and notes */}
        <div className="flex items-center gap-4 mb-5">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100">
            <Clock className="w-3 h-3 text-indigo-500" />
            <span className="text-[10px] font-semibold text-indigo-600">Durata proposta: {aiData.durata}</span>
          </div>
        </div>
        <div className="p-3 rounded-xl bg-muted/20 border border-border/30 mb-5">
          <div className="flex items-start gap-2">
            <Brain className="w-3.5 h-3.5 text-violet-400 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-muted-foreground italic">{aiData.note}</p>
          </div>
        </div>

        {/* Action buttons */}
        {!accepted && !modifying && (
          <div className="flex items-center gap-3" data-testid="ai-action-buttons">
            <Button
              onClick={onAccept}
              className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11"
              data-testid="accept-suggestion-btn"
            >
              <CheckCircle2 className="w-4 h-4" />Accetta Suggerimento
            </Button>
            <Button
              variant="outline"
              onClick={onModify}
              className="flex-1 gap-2 rounded-xl h-11"
              data-testid="modify-suggestion-btn"
            >
              <Edit3 className="w-4 h-4" />Modifica Manualmente
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════
   MANUAL EDIT FORM (Feedback Loop)
   ═══════════════════════════════ */
function ManualEditForm({ aiData, onCancel, onSave }) {
  const [motivazione, setMotivazione] = useState('');
  const [interventi, setInterventi] = useState(
    aiData.interventi.map((int) => ({ ...int }))
  );
  const [durata, setDurata] = useState(aiData.durata);

  const addRow = () => setInterventi(prev => [...prev, { professionista: '', frequenza: '', prestazione: '' }]);
  const removeRow = (i) => setInterventi(prev => prev.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => setInterventi(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const canSave = motivazione.trim().length > 0 && interventi.every(r => r.professionista && r.frequenza && r.prestazione);

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl border-l-4 border-l-amber-400" data-testid="manual-edit-form">
      <CardContent className="p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
            <Edit3 className="w-4 h-4 text-amber-500" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">Modifica Manuale PAI</h2>
            <p className="text-[10px] text-muted-foreground">Personalizza gli interventi proposti dall'AI</p>
          </div>
        </div>

        {/* Mandatory motivation field */}
        <div className="mb-5 p-4 rounded-xl bg-amber-50/50 border border-amber-200/60" data-testid="motivation-section">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <label className="text-xs font-bold text-amber-700">Motivazione scostamento AI *</label>
          </div>
          <textarea
            className="w-full h-24 rounded-xl border border-amber-200 bg-white p-3 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-amber-300/50"
            placeholder="Descrivi perche il suggerimento AI non e adeguato e quali modifiche intendi apportare..."
            value={motivazione}
            onChange={(e) => setMotivazione(e.target.value)}
            data-testid="motivation-textarea"
          />
          <p className="text-[9px] text-amber-600 mt-1.5 italic flex items-center gap-1">
            <Brain className="w-3 h-3" />
            La tua motivazione e fondamentale per il continuo addestramento del modello clinico predittivo.
          </p>
        </div>

        {/* Editable interventions */}
        <div className="space-y-3 mb-5" data-testid="editable-interventions">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground">Interventi</span>
            <Button variant="outline" size="sm" className="gap-1.5 text-xs h-7 rounded-lg" onClick={addRow} data-testid="add-intervention-btn">
              <Plus className="w-3 h-3" />Aggiungi
            </Button>
          </div>
          {interventi.map((int, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_2fr_auto] gap-2 items-center" data-testid={`edit-intervention-${i}`}>
              <input
                className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Professionista"
                value={int.professionista}
                onChange={(e) => updateRow(i, 'professionista', e.target.value)}
              />
              <input
                className="h-9 w-28 rounded-lg border border-border bg-card px-2.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Frequenza"
                value={int.frequenza}
                onChange={(e) => updateRow(i, 'frequenza', e.target.value)}
              />
              <input
                className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Prestazione"
                value={int.prestazione}
                onChange={(e) => updateRow(i, 'prestazione', e.target.value)}
              />
              {interventi.length > 1 && (
                <button onClick={() => removeRow(i)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors" data-testid={`remove-intervention-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Duration */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-foreground mb-1.5 block">Durata proposta</label>
          <input
            className="h-9 w-40 rounded-lg border border-border bg-card px-2.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            value={durata}
            onChange={(e) => setDurata(e.target.value)}
            data-testid="edit-duration"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-xl" onClick={onCancel} data-testid="cancel-edit-btn">Annulla</Button>
          <Button
            className="flex-1 gap-2 rounded-xl h-11 bg-amber-500 hover:bg-amber-600 text-white"
            disabled={!canSave}
            onClick={() => { onSave({ interventi, durata, motivazione }); }}
            data-testid="save-manual-btn"
          >
            <CheckCircle2 className="w-4 h-4" />Salva Modifiche
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════
   FIRMA DIGITALE FOOTER
   ═══════════════════════════════ */
function SignatureFooter({ canSign, onSign, signed }) {
  return (
    <Card className={`border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden ${signed ? 'ring-2 ring-emerald-200' : ''}`} data-testid="signature-footer">
      <CardContent className="p-6">
        {signed ? (
          <div className="flex items-center justify-center gap-3 py-3" data-testid="signed-confirmation">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-700">PAI Firmato e Approvato</p>
              <p className="text-[11px] text-muted-foreground">Firma digitale applicata — 18/02/2026 ore {new Date().getHours()}:{String(new Date().getMinutes()).padStart(2, '0')}</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1">
              <p className="text-xs font-semibold text-foreground">Conferma e Firma Digitale</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Approvando il PAI, accetti la responsabilita clinica del piano assistenziale definito.</p>
            </div>
            <Button
              className="gap-2.5 rounded-xl h-12 px-8 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-md"
              disabled={!canSign}
              onClick={onSign}
              data-testid="sign-btn"
            >
              <Lock className="w-4 h-4" />
              <span className="font-semibold">Firma Digitalmente e Approva PAI</span>
              <FileSignature className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════
   PAGINA PRINCIPALE
   ═══════════════════════════════ */
export default function CaseManagerPAIBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const patient = patientDetails[id];
  const builderData = paiBuilderData[id];

  const [mode, setMode] = useState('initial'); // initial | accepted | modifying | saved
  const [version, setVersion] = useState('V1.0');
  const [versionLabel, setVersionLabel] = useState('Bozza AI');
  const [signed, setSigned] = useState(false);

  if (!patient || !builderData) {
    return (
      <AppLayout role="case-manager">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-sm text-muted-foreground">Dati PAI Builder non disponibili per questo paziente.</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/case-manager')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Torna al Cruscotto
          </Button>
        </div>
      </AppLayout>
    );
  }

  const handleAccept = () => {
    setMode('accepted');
    setVersion('V1.0');
    setVersionLabel('Approvata');
    toast.success('Suggerimento AI accettato', { description: 'Il PAI e stato confermato con i parametri suggeriti.' });
  };

  const handleModify = () => {
    setMode('modifying');
  };

  const handleSaveManual = (data) => {
    setMode('saved');
    setVersion('V1.1');
    setVersionLabel('Modificata');
    toast.success('Modifiche salvate', { description: `Motivazione registrata. Versione aggiornata a V1.1.` });
  };

  const handleCancelEdit = () => {
    setMode('initial');
  };

  const handleSign = () => {
    setSigned(true);
    toast.success('PAI firmato digitalmente', { description: 'Il documento e stato approvato e archiviato.' });
  };

  const canSign = mode === 'accepted' || mode === 'saved';

  return (
    <AppLayout role="case-manager">
      <div className="space-y-6" data-testid="pai-builder-page">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(`/case-manager/paziente/${id}`)}
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group"
            data-testid="back-to-patient"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            Torna alla Scheda Paziente
          </button>
          <Badge
            className="text-[11px] font-bold px-3 py-1.5 border-0 rounded-lg"
            style={{
              backgroundColor: signed ? '#10B98120' : mode === 'accepted' || mode === 'saved' ? '#8B5CF618' : '#94A3B818',
              color: signed ? '#10B981' : mode === 'accepted' || mode === 'saved' ? '#8B5CF6' : '#64748B',
            }}
            data-testid="version-badge"
          >
            Versione: {version} ({signed ? 'Firmata' : versionLabel})
          </Badge>
        </div>

        {/* Page title */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground" data-testid="pai-builder-title">PAI Builder</h1>
          <p className="text-sm text-muted-foreground mt-1">Piano Assistenziale Individuale — {patient.cognome} {patient.nome}</p>
        </div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          {/* Left: InterRAI Summary */}
          <InterRAISummary findings={builderData.interraiFindings} patient={patient} />

          {/* Right: AI + Form */}
          <div className="space-y-6">
            <AISuggestionBox
              aiData={builderData.aiSuggestion}
              onAccept={handleAccept}
              onModify={handleModify}
              accepted={mode === 'accepted'}
              modifying={mode === 'modifying'}
            />

            <AnimatePresence>
              {mode === 'modifying' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <ManualEditForm aiData={builderData.aiSuggestion} onCancel={handleCancelEdit} onSave={handleSaveManual} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Signature Footer */}
        <SignatureFooter canSign={canSign && !signed} onSign={handleSign} signed={signed} />
      </div>
    </AppLayout>
  );
}
